<template>
  <div>
    <slot v-bind="{ image, handleOpen }">
      <div class="w-full flex items-center justify-center relative overflow-hidden h-48 border rounded">
        <div class="h-full w-full absolute inset-0 blur">
          <img v-if="image" :src="image" class="object-cover object-center w-full h-full scale-101">
        </div>

        <button class="z-10 px-3 py-2 rounded-md bg-blue-700 text-white hover:bg-blue-500 transition-all flex items-center space-x-2" @click.stop.prevent="handleOpen">
          <span>{{ $t('components.elements.annotate.index.edit') }}</span>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
          </svg>
        </button>
      </div>
    </slot>

    <Teleport :to="'#' + (portal || '')" :disabled="!portal">
      <div
        v-if="active"
        class="h-[100dvh] w-screen fixed inset-0 bg-gray-100 select-none"
        style="z-index: 100000; touch-action: pan-x pan-y;"
      >
        <!-- Controls -->
        <div class="w-full max-w-xs z-20 absolute top-0 left-1/2 -translate-x-1/2 px-4 my-4 sm:my-8 flex items-center justify-between">
          <div class="flex items-center space-x-1">
            <button
              class="h-9 w-9 rounded-full bg-gray-900 text-white flex items-center justify-center"
              :class="!_value || _value.layers.length === 0 ? 'opacity-70' : ''"
              @click="handleUndo"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
            </button>

            <button
              class="h-9 w-9 rounded-full bg-gray-900 text-white flex items-center justify-center"
              :class="state.redo.length === 0 ? 'opacity-70' : ''"
              @click="handleRedo"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
              </svg>
            </button>

            <div class="w-4" />

            <button
              class="h-9 w-9 rounded-full bg-gray-900 flex items-center justify-center"
              :class="panEnabled ? 'text-blue-600' : 'text-white'"
              @click="handleTogglePan"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002" />
              </svg>
            </button>
          </div>

          <div class="flex items-center space-x-2">
            <button
              class="h-9 rounded-xl bg-blue-700 text-white flex items-center justify-center px-3 font-semibold space-x-2"
              @click="handleSave"
            >
              <span class="mt-px">{{ $t('general.done') }}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Tools -->
        <div v-if="tools" class="z-20 absolute bottom-0 left-1/2 -translate-x-1/2 my-4 sm:my-8 flex flex-col items-center">
          <div v-if="tools[tool] && tools[tool].showSettings" class="bg-gray-900 rounded-xl shadow-sm p-3 inline-flex items-center space-x-2 w-auto mx-auto mb-3">
            <div v-for="(setting, key) in tools[tool].settings" :key="key">
              <component :is="setting.component" />
            </div>
          </div>

          <div v-if="Object.keys(tools).length > 1" class="bg-gray-900 rounded-xl shadow-sm p-3 flex items-center space-x-4">
            <button
              v-for="(item, key) in tools"
              :key="key"
              :class="[
                'h-8 w-8 rounded-full flex items-center justify-center',
                tool === key && !panEnabled ? 'text-blue-600' : 'text-white'
              ]"
              @click="handleSelectTool(key)"
            >
              <span v-html="item.icon" />
            </button>
          </div>
        </div>


        <div
          ref="panzoomRef"
          :class="ready ? 'opacity-100' : 'opacity-0'"
          :style="{
            height: _height,
            width: _width,
            maxHeight: _height,
            maxWidth: _width,
            minHeight: _height,
            minWidth: _width
          }"
        >
          <div
            class="relative transition-all duration-300 ease-in-out"
            :style="styles"
          >
            <img
              ref="background"
              :src="image"
              class="h-full w-full absolute inset-0 object-contain"

              @load="initCanvas"
            >

            <canvas
              ref="canvas"
              class="w-full h-full absolute inset-0"
              :class="[
                panEnabled ? 'cursor-grab pointer-events-none': 'cursor-none'
              ]"
              :height="_height"
              :width="_width"
              :style="styles"
              @pointerdown="handlePointerDown"
              @pointermove="handlePointerMove"
              @pointerup="handlePointerUp"

              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
            />

            <canvas
              ref="canvas_cursor"
              :height="_height"
              :width="_width"
              :style="styles"
              class="w-full h-full absolute inset-0 z-10 pointer-events-none"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
export default { name: 'OvatuAnnotate' }
</script>

<script setup lang="ts">
import panzoom from 'panzoom'

import Brush from './tools/Brush'
import Eraser from './tools/Eraser'
import Stamp from './tools/Stamp'

interface Props {
  modelValue?: string | null
  image?: string | null
  label?: string
  config?: Record<string, any>
  portal?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  image: null,
  label: '',
  config: () => ({
    allowHistory: false,
    tools: {
      brush: {
        size: 4,
        colours: [
          'black',
          '#dc2626',
          '#0284c7',
          '#059669',
        ],
      },
      eraser: {
        size: 10,
      },
      stamp: {
        size: 20,
        stamps: [
          '\u{1D7ED}',
          '\u{1D7EE}',
          '\u{1D7EF}',
        ],
      },
    },
  }),
  portal: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const panzoomRef = ref<any>(null)
const background = ref<any>(null)
const canvas = ref<any>(null)
const canvas_cursor = ref<any>(null)

const active = ref(false)
const ready = ref(false)

const tool = ref<string | null>(null)
const tools = ref<Record<string, any> | null>(null)

let panzoomInstance: any = null
const panEnabled = ref(false)

const mouse = reactive({
  x: 0,
  y: 0,
})
const cursor = reactive({
  lastX: 0,
  lastY: 0,
})
const state = reactive({
  redo: [] as any[],
})

const _value = computed({
  get () {
    return props.modelValue?.length ? JSON.parse(props.modelValue) : null
  },
  set (value: any) {
    emit('update:modelValue', JSON.stringify(value))
  },
})

const _width = computed(() => _value.value?.width + 'px' || '')
const _height = computed(() => _value.value?.height + 'px' || '')

const styles = computed(() => ({
  height: _height.value,
  width: _width.value,
  maxHeight: _height.value,
  maxWidth: _width.value,
  minHeight: _height.value,
  minWidth: _width.value,
}))

watch(panEnabled, (value) => {
  if (value) {
    panzoomInstance?.resume()
  } else {
    panzoomInstance?.pause()
  }
})

function handleOpen () {
  ready.value = false
  active.value = true
}

function initCanvas (e: Event) {
  nextTick(() => {
    const bg = background.value

    const height = bg.naturalHeight
    const width = bg.naturalWidth

    if (!_value.value) {
      _value.value = {
        layers: [],
        width,
        height,
        format: 'annotation',
        version: 1,
      }
    }

    setTimeout(() => {
      const context = {
        canvas: canvas.value,
        canvas_cursor: canvas_cursor.value,
        mouse,
        cursor,
        get layers () {
          return _value.value.layers
        },
        set layers (value: any) {
          _value.value = {
            ..._value.value,
            layers: value,
          }
        },
      }

      tools.value = {}

      if (props.config.draw?.enabled) {
        tools.value.brush = new Brush(context, props.config.draw)

        if (!tool.value) {
          tool.value = 'brush'
        }
      }

      if (props.config.erase?.enabled) {
        tools.value.eraser = new Eraser(context, props.config.erase)

        if (!tool.value) {
          tool.value = 'eraser'
        }
      }

      if (props.config.stamps?.enabled) {
        tools.value.stamp = new Stamp(context, props.config.stamps)

        if (!tool.value) {
          tool.value = 'stamp'
        }
      }

      requestAnimationFrame(renderCursor)

      if (_value.value && _value.value.layers.length > 0) {
        for (const layer of _value.value.layers) {
          if (layer.type === 'line') {
            tools.value.brush.redraw(layer)
          } else if (layer.type === 'eraser') {
            tools.value.eraser.redraw(layer)
          } else if (layer.type === 'stamp') {
            tools.value.stamp.redraw(layer)
          }
        }
      }

      initPanzoom()
    }, 500)
  })
}

function initPanzoom () {
  nextTick(() => {
    const element = panzoomRef.value

    panzoomInstance = panzoom(element, {
      autocenter: true,
      beforeMouseDown: (e: any) => {
        return !panEnabled.value
      },
      onTouch: (e: any) => {
        return false
      },
    })

    panzoomInstance.pause()

    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    const zoom = 0.7

    panzoomInstance.setTransformOrigin({ x: 0.5, y: 0.5 })
    panzoomInstance.zoomTo(windowWidth / 2, windowHeight / 2, zoom)

    panzoomInstance.setTransformOrigin(null)

    ready.value = true
  })
}

function destroyPanzoom () {
  panzoomInstance?.dispose()
}

function handleTogglePan () {
  panEnabled.value = !panEnabled.value
}

function handleSelectTool (key: string) {
  tool.value = key
  panEnabled.value = false
}

function handleUndo () {
  if (_value.value.layers.length > 0) {
    const layer = _value.value.layers.pop()
    state.redo.push(layer)

    const c = canvas.value
    const ctx = c.getContext('2d')

    ctx.clearRect(0, 0, c.width, c.height)

    if (_value.value.layers.length > 0) {
      for (const l of _value.value.layers) {
        if (l.type === 'line') {
          tools.value!.brush.redraw(l)
        } else if (l.type === 'eraser') {
          tools.value!.eraser.redraw(l)
        } else if (l.type === 'stamp') {
          tools.value!.stamp.redraw(l)
        }
      }
    }
  }
}

function handleRedo () {
  if (state.redo.length > 0) {
    const layer = state.redo.pop()

    _value.value.layers.push(layer)

    if (layer.type === 'line') {
      tools.value!.brush.redraw(layer)
    } else if (layer.type === 'eraser') {
      tools.value!.eraser.redraw(layer)
    } else if (layer.type === 'stamp') {
      tools.value!.stamp.redraw(layer)
    }
  }
}

function handleClear () {
  const c = canvas.value
  const ctx = c.getContext('2d')

  ctx.clearRect(0, 0, c.width, c.height)
}

function handleSave () {
  active.value = false
}

function renderCursor () {
  const t = tools.value?.[tool.value!]

  if (t?.drawCursor) {
    t.drawCursor()
  }

  requestAnimationFrame(renderCursor)
}

function handlePointerDown (e: PointerEvent) {
  if (e.pointerType !== 'touch') {
    const x = e.offsetX
    const y = e.offsetY

    const t = tools.value?.[tool.value!]

    if (t?.pointerDown) {
      t.pointerDown({ x, y })
    }
  }
}

function handlePointerMove (e: PointerEvent) {
  if (e.pointerType !== 'touch') {
    const x = e.offsetX
    const y = e.offsetY

    mouse.x = x
    mouse.y = y

    const t = tools.value?.[tool.value!]

    if (t?.pointerMove) {
      t.pointerMove({ x, y })
    }
  }
}

function handlePointerUp (e: PointerEvent) {
  if (e.pointerType !== 'touch') {
    const x = e.offsetX
    const y = e.offsetY

    const t = tools.value?.[tool.value!]

    if (t?.pointerUp) {
      t.pointerUp({ x, y })
    }
  }
}

function handleTouchStart (e: TouchEvent) {
  const t = tools.value?.[tool.value!]

  const rect = (e.target as HTMLElement).getBoundingClientRect()
  const transform = panzoomInstance.getTransform()
  const scale = transform.scale

  const x = (e.targetTouches[0].pageX - rect.left) / scale
  const y = (e.targetTouches[0].pageY - rect.top) / scale

  if (t?.pointerDown) {
    t.pointerDown({ x, y })
  }
}

function handleTouchMove (e: TouchEvent) {
  const t = tools.value?.[tool.value!]

  const rect = (e.target as HTMLElement).getBoundingClientRect()
  const transform = panzoomInstance.getTransform()
  const scale = transform.scale

  const x = (e.targetTouches[0].pageX - rect.left) / scale
  const y = (e.targetTouches[0].pageY - rect.top) / scale

  mouse.x = x
  mouse.y = y

  if (t?.pointerMove) {
    t.pointerMove({ x, y })
  }
}

function handleTouchEnd (e: TouchEvent) {
  const t = tools.value?.[tool.value!]

  if (t?.pointerUp) {
    t.pointerUp()
  }
}

function lerp (a: number, b: number, n: number) {
  return (1 - n) * a + n * b
}
</script>

<style>
/* prevent pull-to-refresh for Safari 16+ */
@media screen and (pointer: coarse) {
  @supports (-webkit-backdrop-filter: blur(1px)) and (overscroll-behavior-y: none)  {
    html {
      min-height: 100.3%;
      overscroll-behavior-y: none;
    }
  }
}
/* prevent pull-to-refresh for Safari 9~15 */
@media screen and (pointer: coarse) {
  @supports (-webkit-backdrop-filter: blur(1px)) and (not (overscroll-behavior-y: none))  {
    html {
      height: 100%;
      overflow: hidden;
    }
    body {
      margin: 0px;
      max-height: 100%; /* or `height: calc(100% - 16px);` if body has default margin */
      overflow: auto;
      -webkit-overflow-scrolling: touch;
    }
    /* in this case to disable pinch-zoom, set `touch-action: pan-x pan-y;` on `body` instead of `html` */
  }
}

/* prevent pull-to-refresh for Chrome 63+ */
body{
  overscroll-behavior-y: none;
}
</style>
